import { DefaultError } from '@tanstack/query-core';
import { UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchConnectionMappings } from 'api/integrations';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { IntegrationMapping, IntegrationMappingContext } from 'types/domain';

interface AggregatedAccountConnectionMappings {
    mappings: IntegrationMapping[];
    bloomerang: IntegrationMapping[] | undefined;
    thankview: IntegrationMapping[] | undefined;
}

interface Options
    extends Omit<
        UseSuspenseQueryOptions<
            IntegrationMapping[],
            DefaultError,
            AggregatedAccountConnectionMappings,
            any
        >,
        'queryKey' | 'select'
    > {}

const useCurrentAccountIntegrationMappings = (
    type: IntegrationMappingContext,
    options: Options = {} as Options,
) => {
    const { accountId } = useCurrentAccount();

    const { data, ...rest } = useSuspenseQuery({
        queryKey: accountQueryKeys.integrationMappings(accountId, type),
        queryFn: () => fetchConnectionMappings(accountId, type),
        select: aggregateIntegrationMappings,
        ...options,
    });

    return { data, ...rest };
};

const aggregateIntegrationMappings = (
    mappings: IntegrationMapping[],
): AggregatedAccountConnectionMappings => {
    const getValueByContext = (context: string) =>
        mappings.filter(item => item.context === context);

    return {
        mappings,
        bloomerang: getValueByContext('bloomerang'),
        thankview: getValueByContext('thankview'),
    };
};

export { useCurrentAccountIntegrationMappings };
